import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Check, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import OutlineUploader from "./OutlineUploader";
import OutlineCopilot from "./OutlineCopilot";
import ChapterPlanTable from "./ChapterPlanTable";
import GenerationProgress from "./GenerationProgress";
import ThumbnailUploader from "./ThumbnailUploader";

const STEPS = [
    { key: "details", label: "Details" },
    { key: "outline", label: "Outline" },
    { key: "chapters", label: "Chapters" },
    { key: "generate", label: "Generate" },
];

function Stepper({ active }) {
    const activeIdx = STEPS.findIndex((s) => s.key === active);
    return (
        <div className="flex items-center gap-2 mb-6" data-testid="wizard-stepper">
            {STEPS.map((s, idx) => {
                const done = idx < activeIdx;
                const current = idx === activeIdx;
                return (
                    <React.Fragment key={s.key}>
                        <div className="flex items-center gap-2">
                            <div
                                className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                                    done
                                        ? "bg-accent text-accent-foreground"
                                        : current
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-secondary text-muted-foreground"
                                }`}
                            >
                                {done ? <Check className="h-3 w-3" /> : idx + 1}
                            </div>
                            <span
                                className={`text-xs uppercase tracking-wider ${
                                    current ? "text-primary font-semibold" : "text-muted-foreground"
                                }`}
                            >
                                {s.label}
                            </span>
                        </div>
                        {idx < STEPS.length - 1 && (
                            <div className="flex-1 h-px bg-border" />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

const DEFAULT_DETAILS = {
    title: "",
    description: "",
    subject: "",
    level: "beginner",
    price: 99,
    price_international: "",
    thumbnail_url: "",
    instructor_id: "",
};

const DEFAULT_PLAN = [
    { title: "", hours: 2 },
    { title: "", hours: 2 },
    { title: "", hours: 2 },
];

export default function CreateProgramWizard({
    open,
    onOpenChange,
    instructors,
    onCreated,
    apiPath = "/admin/programs",
    hideInstructorPicker = false,
}) {
    const navigate = useNavigate();
    const [step, setStep] = useState("details");
    const [details, setDetails] = useState(DEFAULT_DETAILS);
    const [programId, setProgramId] = useState(null);
    const [outline, setOutline] = useState(null);
    const [plan, setPlan] = useState(DEFAULT_PLAN);
    const [lessonsPerHour, setLessonsPerHour] = useState(3);
    const [questionsPerQuiz, setQuestionsPerQuiz] = useState(10);
    const [submitting, setSubmitting] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [genResult, setGenResult] = useState(null);

    const reset = () => {
        setStep("details");
        setDetails(DEFAULT_DETAILS);
        setProgramId(null);
        setOutline(null);
        setPlan(DEFAULT_PLAN);
        setLessonsPerHour(3);
        setQuestionsPerQuiz(10);
        setSubmitting(false);
        setJobId(null);
        setGenResult(null);
    };

    const close = (v) => {
        if (!v) reset();
        onOpenChange(v);
    };

    // STEP 1 → create program, move to outline
    const submitDetails = async () => {
        if (!details.title.trim()) {
            toast.error("Title is required");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...details, price: parseFloat(details.price) };
            if (!payload.instructor_id) delete payload.instructor_id;
            if (
                payload.price_international === "" ||
                payload.price_international == null
            ) {
                delete payload.price_international;
            } else {
                payload.price_international = parseFloat(payload.price_international);
            }
            const { data } = await api.post(apiPath, payload);
            setProgramId(data.id);
            toast.success("Program created — now upload its outline");
            onCreated?.();
            setStep("outline");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        } finally {
            setSubmitting(false);
        }
    };

    // STEP 4 → kick off async job
    const runGeneration = async () => {
        const cleanPlan = plan
            .filter((c) => c.title.trim())
            .map((c) => ({ title: c.title.trim(), hours: Number(c.hours) || 1 }));
        if (cleanPlan.length === 0) {
            toast.error("Add at least one chapter to the plan");
            return;
        }
        setStep("generate");
        setSubmitting(true);
        setJobId(null);
        setGenResult(null);
        try {
            const { data } = await api.post(
                `/programs/${programId}/generate-from-outline`,
                {
                    chapters: cleanPlan,
                    lessons_per_hour: lessonsPerHour,
                    questions_per_quiz: questionsPerQuiz,
                    replace_existing: false,
                },
            );
            setJobId(data.job_id);
        } catch (e) {
            const errorText = e?.response?.data?.detail || "Could not start generation";
            toast.error(errorText);
            setGenResult({ failed: true, error: errorText });
            setSubmitting(false);
        }
    };

    const handleGenerationComplete = (job) => {
        setSubmitting(false);
        setGenResult(job);
        if (job.failures > 0) {
            toast.warning(
                `${job.chapters_created}/${job.total} chapters generated. ${job.failures} failed.`,
            );
        } else {
            toast.success(
                `Generated ${job.chapters_created} chapters and ${job.lessons_created} lessons.`,
            );
        }
    };

    const handleGenerationError = (job) => {
        setSubmitting(false);
        const errorText = typeof job?.error === "string" ? job.error : "Generation failed";
        setGenResult({ ...(job || {}), failed: true, error: errorText });
        toast.error(errorText);
    };

    const goToEditor = () => {
        const id = programId;
        reset();
        onOpenChange(false);
        navigate(`/instructor/programs/${id}`);
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent
                className="max-w-3xl max-h-[90vh] overflow-y-auto"
                data-testid="create-program-wizard"
            >
                <DialogHeader>
                    <DialogTitle className="font-display">Create program</DialogTitle>
                    <DialogDescription>
                        Create a new program, upload its outline, plan chapters, then let AI
                        draft detailed lessons and quizzes — all editable afterwards.
                    </DialogDescription>
                </DialogHeader>

                <Stepper active={step} />

                {step === "details" && (
                    <div className="space-y-3" data-testid="wizard-step-details">
                        <div>
                            <Label>Title</Label>
                            <Input
                                value={details.title}
                                onChange={(e) => setDetails({ ...details, title: e.target.value })}
                                data-testid="wizard-title-input"
                            />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea
                                rows={3}
                                value={details.description}
                                onChange={(e) =>
                                    setDetails({ ...details, description: e.target.value })
                                }
                                data-testid="wizard-desc-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Subject</Label>
                                <Input
                                    value={details.subject}
                                    onChange={(e) =>
                                        setDetails({ ...details, subject: e.target.value })
                                    }
                                />
                            </div>
                            <div>
                                <Label>Level</Label>
                                <Select
                                    value={details.level}
                                    onValueChange={(v) => setDetails({ ...details, level: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="beginner">Beginner</SelectItem>
                                        <SelectItem value="intermediate">Intermediate</SelectItem>
                                        <SelectItem value="advanced">Advanced</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Domestic tuition (CAD)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={details.price}
                                    onChange={(e) =>
                                        setDetails({ ...details, price: e.target.value })
                                    }
                                    data-testid="wizard-price-domestic"
                                />
                            </div>
                            <div>
                                <Label>International tuition (CAD)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    placeholder="Optional — falls back to domestic"
                                    value={details.price_international}
                                    onChange={(e) =>
                                        setDetails({
                                            ...details,
                                            price_international: e.target.value,
                                        })
                                    }
                                    data-testid="wizard-price-international"
                                />
                            </div>
                        </div>
                        {hideInstructorPicker ? null : (
                            <div>
                                <Label>Instructor</Label>
                                <Select
                                    value={details.instructor_id}
                                    onValueChange={(v) =>
                                        setDetails({ ...details, instructor_id: v })
                                    }
                                >
                                    <SelectTrigger data-testid="wizard-instructor-select">
                                        <SelectValue placeholder="Assign…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(instructors || []).map((u) => (
                                            <SelectItem key={u.id} value={u.id}>
                                                {u.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div>
                            <ThumbnailUploader
                                value={details.thumbnail_url}
                                onChange={(url) => setDetails({ ...details, thumbnail_url: url })}
                                testIdPrefix="wizard-thumb"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => close(false)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={submitDetails}
                                disabled={submitting}
                                className="bg-primary hover:bg-primary/90"
                                data-testid="wizard-next-from-details"
                            >
                                {submitting ? "Creating…" : "Next"}
                            </Button>
                        </div>
                    </div>
                )}

                {step === "outline" && (
                    <div className="space-y-5" data-testid="wizard-step-outline">
                        <OutlineCopilot
                            programId={programId}
                            onExpanded={(text) => setOutline({
                                preview: text,
                                filename: "ai-expanded.txt",
                                char_count: text.length,
                            })}
                        />
                        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center gap-2">
                            <span className="h-px flex-1 bg-border" />
                            or upload a PDF
                            <span className="h-px flex-1 bg-border" />
                        </div>
                        <OutlineUploader
                            programId={programId}
                            onUploaded={setOutline}
                            initialPreview={outline?.preview}
                            initialFilename={outline?.filename}
                        />
                        <div className="flex justify-between gap-2 pt-2">
                            <Button variant="ghost" onClick={goToEditor} data-testid="wizard-skip-to-editor">
                                Skip & add content manually
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setStep("details")}
                                    data-testid="wizard-back-details"
                                >
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Back
                                </Button>
                                <Button
                                    onClick={() => setStep("chapters")}
                                    disabled={!outline}
                                    className="bg-primary hover:bg-primary/90"
                                    data-testid="wizard-next-from-outline"
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {step === "chapters" && (
                    <div className="space-y-5" data-testid="wizard-step-chapters">
                        <ChapterPlanTable
                            chapters={plan}
                            onChange={setPlan}
                            lessonsPerHour={lessonsPerHour}
                        />
                        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
                            <div>
                                <Label>Lessons per hour</Label>
                                <Select
                                    value={String(lessonsPerHour)}
                                    onValueChange={(v) => setLessonsPerHour(Number(v))}
                                >
                                    <SelectTrigger data-testid="wizard-lph-trigger">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="2">2 (longer lessons)</SelectItem>
                                        <SelectItem value="3">3 (default)</SelectItem>
                                        <SelectItem value="4">4 (shorter lessons)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>MCQs per chapter quiz</Label>
                                <Select
                                    value={String(questionsPerQuiz)}
                                    onValueChange={(v) => setQuestionsPerQuiz(Number(v))}
                                >
                                    <SelectTrigger data-testid="wizard-qpq-trigger">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="5">5</SelectItem>
                                        <SelectItem value="10">10 (default · pass 7/10)</SelectItem>
                                        <SelectItem value="15">15</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="flex justify-between gap-2 pt-2">
                            <Button variant="ghost" onClick={goToEditor} data-testid="wizard-skip-to-editor-2">
                                Skip & add content manually
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setStep("outline")}
                                    data-testid="wizard-back-outline"
                                >
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Back
                                </Button>
                                <Button
                                    onClick={runGeneration}
                                    disabled={submitting}
                                    className="bg-primary hover:bg-primary/90"
                                    data-testid="wizard-generate-btn"
                                >
                                    <Sparkles className="h-4 w-4 mr-2" />
                                    Generate with AI
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {step === "generate" && (
                    <div className="space-y-5 py-4" data-testid="wizard-step-generate">
                        {jobId && submitting && (
                            <GenerationProgress
                                jobId={jobId}
                                onComplete={handleGenerationComplete}
                                onError={handleGenerationError}
                            />
                        )}
                        {!submitting && genResult && !genResult.failed && (
                            <div className="text-center py-4">
                                <div className="h-14 w-14 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto mb-3">
                                    <Check className="h-7 w-7" />
                                </div>
                                <div className="font-display text-2xl text-primary">
                                    Program ready
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {genResult.chapters_created} chapter
                                    {genResult.chapters_created === 1 ? "" : "s"} and{" "}
                                    {genResult.lessons_created} lesson
                                    {genResult.lessons_created === 1 ? "" : "s"} were created.
                                    {genResult.failures > 0
                                        ? ` ${genResult.failures} chapter(s) failed and can be retried in the editor.`
                                        : ""}
                                </p>
                                <div className="flex gap-2 justify-center mt-6">
                                    <Button
                                        onClick={goToEditor}
                                        className="bg-primary hover:bg-primary/90"
                                        data-testid="wizard-open-editor-btn"
                                    >
                                        Open editor
                                    </Button>
                                </div>
                            </div>
                        )}
                        {!submitting && genResult?.failed && (
                            <div className="py-4">
                                <div className="text-center">
                                    <div className="font-display text-lg text-destructive">
                                        Generation failed
                                    </div>
                                    {genResult.error && typeof genResult.error === "string" && (
                                        <p className="text-sm text-foreground/85 mt-2 max-w-md mx-auto">
                                            {genResult.error}
                                        </p>
                                    )}
                                    {genResult.error &&
                                        /Budget has been exceeded|Max budget/i.test(
                                            genResult.error,
                                        ) && (
                                            <div className="mt-3 text-sm text-foreground/85 bg-amber-50 border border-amber-200 rounded-sm p-3 text-left max-w-md mx-auto">
                                                Your Emergent LLM key budget is exhausted. Open
                                                your profile →{" "}
                                                <span className="font-medium">
                                                    Universal Key → Add Balance
                                                </span>{" "}
                                                (or enable auto top-up), then retry.
                                            </div>
                                        )}
                                    <p className="text-sm text-muted-foreground mt-3">
                                        You can still open the editor and add content manually, or
                                        go back and retry.
                                    </p>
                                    <div className="flex gap-2 justify-center mt-6">
                                        <Button
                                            variant="outline"
                                            onClick={() => setStep("chapters")}
                                            data-testid="wizard-retry-btn"
                                        >
                                            Back to plan
                                        </Button>
                                        <Button
                                            onClick={goToEditor}
                                            className="bg-primary hover:bg-primary/90"
                                        >
                                            Open editor
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
